/**
 * Per-customer load-email settings (Phase 17 / Task 17.3, DEC-324 D-12).
 *
 * Two independent settings — "email after each haul" (on/off) and a summary schedule —
 * each of which may also "use the hauler default". The panel shows what actually applies
 * (the effective value) so nobody has to work out inheritance in their head. Saves on
 * change, like AccountingExportEditor. Read-only without MANAGE_HAULER_CATALOG.
 */

import * as React from 'react';
import { toast } from 'sonner';
import {
  CADENCE_LABEL,
  SUMMARY_CADENCES,
  useOperatorNotificationSettings,
  usePutOperatorSettings,
  type OperatorNotificationSettings,
  type SummaryCadence,
} from './api';
import { cardStyle, helpText, inputStyle, labelStyle } from './styles';

const INHERIT = '__inherit__';

function effectiveSentence(s: OperatorNotificationSettings): string {
  const each = s.effective.eachHaulEnabled;
  const summary = s.effective.summaryCadence;
  if (!each && summary === 'OFF') {
    return 'No Haul Confirmation emails go to this customer (urgent loads included).';
  }
  const parts: string[] = [];
  if (each) parts.push('an email after every haul');
  if (summary !== 'OFF') parts.push(`a Haul Summary ${CADENCE_LABEL[summary].toLowerCase()}`);
  return `Contacts get ${parts.join(' and ')}. Urgent loads email right away.`;
}

export function OperatorNotificationsPanel({
  operatorId,
  canManage,
}: {
  readonly operatorId: string;
  readonly canManage: boolean;
}): React.JSX.Element {
  const { data, isLoading, isError } = useOperatorNotificationSettings(operatorId);
  const put = usePutOperatorSettings(operatorId);
  const s = data?.settings;

  const save = (next: {
    eachHaulEnabled: boolean | null;
    summaryCadence: SummaryCadence | null;
  }): void => {
    put.mutate(
      { payload: next },
      {
        onSuccess: () => toast.success('Email settings saved'),
        onError: () => toast.error('Could not save email settings'),
      },
    );
  };

  return (
    <section aria-labelledby="load-email-settings" style={cardStyle}>
      <h3 id="load-email-settings" style={{ fontSize: '18px', margin: '0 0 4px' }}>
        Haul Confirmation emails
      </h3>
      {isLoading && <p style={helpText}>Loading…</p>}
      {isError && (
        <p style={{ ...helpText, color: 'var(--color-danger)' }}>Could not load settings.</p>
      )}
      {s !== undefined && (
        <>
          <p style={{ ...helpText, marginBottom: '16px' }} data-testid="effective-sentence">
            {effectiveSentence(s)}
          </p>
          <div
            style={{
              display: 'grid',
              gap: '16px',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            }}
          >
            <div>
              <label htmlFor="each-haul" style={labelStyle}>
                Email site contacts after each haul
              </label>
              <select
                id="each-haul"
                style={inputStyle}
                disabled={!canManage || put.isPending}
                value={s.eachHaulEnabled === null ? INHERIT : s.eachHaulEnabled ? 'on' : 'off'}
                onChange={(e): void =>
                  save({
                    eachHaulEnabled: e.target.value === INHERIT ? null : e.target.value === 'on',
                    summaryCadence: s.summaryCadence,
                  })
                }
              >
                <option value={INHERIT}>
                  Hauler default ({s.tenantDefault.eachHaulEnabled ? 'On' : 'Off'})
                </option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </div>
            <div>
              <label htmlFor="summary-cadence" style={labelStyle}>
                Haul Summary
              </label>
              <select
                id="summary-cadence"
                style={inputStyle}
                disabled={!canManage || put.isPending}
                value={s.summaryCadence ?? INHERIT}
                onChange={(e): void =>
                  save({
                    eachHaulEnabled: s.eachHaulEnabled,
                    summaryCadence:
                      e.target.value === INHERIT ? null : (e.target.value as SummaryCadence),
                  })
                }
              >
                <option value={INHERIT}>
                  Hauler default ({CADENCE_LABEL[s.tenantDefault.summaryCadence]})
                </option>
                {SUMMARY_CADENCES.map((c) => (
                  <option key={c} value={c}>
                    {CADENCE_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
